import { ComponentType, PropsWithChildren } from 'react';
import { Link as RouterLink, Location } from 'react-router-dom';
import styled, { css } from 'styled-components';

import { getStyledUtils, StyledUtilsProps } from '../helpers/StyledUtils';
import Icon, { IconName } from './Icon';

interface Props
  extends StyledUtilsProps, Omit<React.HTMLProps<HTMLElement>, 'width' | 'as' | 'muted'> {
  as?: string | ComponentType<any>;
  type?: string;
  target?: string;

  active?: boolean;

  rel?: string;
  to?: string | Partial<Location> | ((location: Location) => Partial<Location> | string);

  primary?: boolean;
  dark?: boolean;
  neutral?: boolean;
  small?: boolean;

  underline?: boolean;
  no_underline?: boolean;

  icon?: IconName;
  reveal_icon_on_hover?: boolean;

  font_weight?: number;
}

const StyledLink = styled.a<Props & { link?: boolean }>(
  ({
    theme,
    link,
    underline,
    no_underline,
    font_weight,
    reveal_icon_on_hover,
    active,
    ...props
  }) => {
    const styles = css`
      ${getStyledUtils}
      border: none;
      background-color: transparent;
      padding: 0;
      margin: 0;
      cursor: pointer;
      text-decoration: ${underline ? 'underline' : 'none'};
      font-weight: ${font_weight || theme.font_weights.medium};
      font-size: ${theme.pxToRem(
        props.small ? theme.font_sizes.s.font_size : theme.font_sizes.m.font_size,
      )};
      line-height: ${theme.pxToRem(
        props.small ? theme.font_sizes.s.line_height : theme.font_sizes.m.line_height,
      )};
      display: inline-flex;
      align-items: center;

      span > {
        display: inline-block;
        line-height: ${theme.pxToRem(
          props.small ? theme.font_sizes.s.line_height : theme.font_sizes.m.line_height,
        )};
      }
      position: relative;
      white-space: nowrap;

      &:hover {
        span:first-child {
          text-decoration: ${no_underline ? 'none' : 'underline'};
        }
      }

      ${active &&
      css`
        span:first-child {
          text-decoration: ${no_underline ? 'none' : 'underline'};
        }
      `}

      ${reveal_icon_on_hover &&
      css`
        svg,
        span:not(:first-child) {
          display: none;
        }

        &:hover {
          svg,
          span:not(:first-child) {
            display: block;
          }
        }
      `}

      ${theme.optionsToCss(
        {
          primary: css`
            color: ${theme.colors.on.neutral.primary};
          `,
          neutral: css`
            color: ${theme.colors.on.neutral.tertiary_neutral};
            &:hover {
              color: ${theme.colors.on.neutral.primary_neutral};
            }
          `,
          dark: css`
            color: ${theme.colors.on.neutral.primary_neutral};
          `,
        },
        props,
        'primary',
      )};

      &:focus-visible {
        outline: 2px solid ${theme.colors.outline.focus.primary};
        outline-offset: 2px;
        border-radius: ${theme.radius.small};
      }
    `;
    return link
      ? css`
          a {
            ${styles}
          }
        `
      : styles;
  },
);

const Link: React.FC<PropsWithChildren<Props>> = ({ to, as, children, icon, target, ...props }) => {
  if (to) {
    return (
      <StyledLink {...props} link as={'span' as any}>
        <RouterLink to={to} target={target}>
          <span>{children}</span>
          {icon && <Icon small icon={icon} right={children ? 1 : 0} pointer />}
        </RouterLink>
      </StyledLink>
    );
  }
  return (
    <StyledLink {...props} as={as || 'a'} target={target}>
      <span>{children}</span>
      {icon && <Icon small icon={icon} right={children ? 1 : 0} pointer />}
    </StyledLink>
  );
};

export default Link;
